import { useEffect, useRef, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import {
  QrCode, ScanLine, IndianRupee, CheckCircle2, XCircle,
  Clock, Ban, AlertTriangle,
} from "lucide-react";

const STATUS_STYLE = {
  PENDING: "bg-amber-100 text-amber-800 border-amber-200",
  PAID: "bg-emerald-100 text-emerald-800 border-emerald-200",
  REJECTED: "bg-red-100 text-red-800 border-red-200",
  FAILED: "bg-slate-200 text-slate-700 border-slate-300",
};

const fmtINR = (n) => (typeof n === "number"
  ? `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "—");

let qrPanelSeq = 0;

function ScanPanel({ onDecoded }) {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState("");
  const instRef = useRef(null);
  const elIdRef = useRef(`vendor-pay-qr-reader-${++qrPanelSeq}`);
  const elId = elIdRef.current;

  const stop = useCallback(async () => {
    const inst = instRef.current;
    instRef.current = null;
    setScanning(false);
    if (!inst) return;
    try {
      if (inst.isScanning) await inst.stop();
      inst.clear();
    } catch { /* already stopped / never started */ }
  }, []);

  const start = useCallback(async () => {
    setError("");
    setScanning(true);
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      // Give the container a paint cycle before the library measures it —
      // starting immediately on a just-mounted node can make html5-qrcode
      // mis-size its internal shading canvas and render solid black.
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const inst = new Html5Qrcode(elId);
      instRef.current = inst;
      await inst.start(
        { facingMode: "environment" },
        {
          fps: 10,
          // Dynamic qrbox (per html5-qrcode docs) — a fixed pixel size can
          // exceed the real viewfinder on some screens and make the shaded
          // overlay cover the entire preview (looks like a black screen).
          qrbox: (viewfinderWidth, viewfinderHeight) => {
            const edge = Math.min(viewfinderWidth, viewfinderHeight);
            const size = Math.max(150, Math.floor(edge * 0.7));
            return { width: size, height: size };
          },
        },
        (decodedText) => { onDecoded(decodedText); stop(); },
        () => {},
      );
    } catch (e) {
      setError(e?.message || "Could not access camera. Use Manual Entry instead.");
      instRef.current = null;
      setScanning(false);
    }
  }, [elId, onDecoded, stop]);

  useEffect(() => () => { stop(); }, [stop]);

  return (
    <div className="space-y-3">
      <style>{`#${elId} video { width: 100% !important; border-radius: 0.5rem; }`}</style>
      <div
        className={`relative rounded-lg overflow-hidden border-2 border-dashed ${scanning ? "border-red-400" : "border-slate-200"} min-h-[220px] bg-slate-950/5`}
        data-testid="qr-reader-viewport"
      >
        {/* html5-qrcode owns every child of this div exclusively — React
            must never render anything inside it, or a later re-render
            will wipe the live video/canvas it inserted (the "black
            screen" bug). */}
        <div id={elId} className="w-full" />
        {!scanning && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center text-slate-400 text-xs py-10">
              <ScanLine className="h-8 w-8 mx-auto mb-2" />
              Camera preview appears here
            </div>
          </div>
        )}
        {scanning && !instRef.current && !error && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center text-slate-400 text-xs">Starting camera…</div>
          </div>
        )}
      </div>
      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 flex items-start gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {error}
        </div>
      )}
      {!scanning ? (
        <Button size="sm" onClick={start} className="w-full" data-testid="start-scan-btn">
          <QrCode className="h-4 w-4 mr-1.5" /> Start Camera Scan
        </Button>
      ) : (
        <Button size="sm" variant="outline" onClick={stop} className="w-full" data-testid="stop-scan-btn">
          Stop Scan
        </Button>
      )}
    </div>
  );
}

function parseUpiUri(text) {
  try {
    const q = text.includes("?") ? text.split("?")[1] : text;
    const params = new URLSearchParams(q);
    const pa = params.get("pa");
    if (!pa) return null;
    return {
      payee_vpa: pa,
      payee_name: params.get("pn") || "",
      amount: params.get("am") ? parseFloat(params.get("am")) : "",
    };
  } catch { return null; }
}

export default function VendorPayments() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [newDlgOpen, setNewDlgOpen] = useState(false);
  const [mode, setMode] = useState("scan"); // scan | manual
  const [form, setForm] = useState({ payee_vpa: "", payee_name: "", amount: "", purpose: "", notes: "" });
  const [source, setSource] = useState("manual");
  const [saving, setSaving] = useState(false);
  const [rejectDlg, setRejectDlg] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const canApprove = user?.role === "super_admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vendor-payments/", { params: { status: statusFilter } });
      setRows(data || []);
    } finally { setLoading(false); }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const onDecoded = (text) => {
    const parsed = parseUpiUri(text);
    if (!parsed) {
      toast.error("QR does not look like a UPI payment code");
      return;
    }
    setForm((f) => ({ ...f, ...parsed, amount: parsed.amount || f.amount }));
    setSource("scan");
    toast.success("QR scanned — verify details below and submit");
  };

  const resetForm = () => {
    setForm({ payee_vpa: "", payee_name: "", amount: "", purpose: "", notes: "" });
    setMode("scan");
    setSource("manual");
  };

  const submit = async () => {
    if (!form.payee_vpa || !form.amount) {
      toast.error("UPI ID and amount are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/vendor-payments/", {
        payee_vpa: form.payee_vpa.trim(),
        payee_name: form.payee_name || null,
        amount: parseFloat(form.amount),
        purpose: form.purpose || null,
        notes: form.notes || null,
        source,
      });
      toast.success("Payment request raised — pending Super Admin approval");
      setNewDlgOpen(false);
      resetForm();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to raise request");
    } finally {
      setSaving(false);
    }
  };

  const approve = async (r) => {
    if (!window.confirm(`Approve payment of ${fmtINR(r.amount)} to ${r.payee_name || r.payee_vpa}? This triggers the payout.`)) return;
    try {
      await api.post(`/vendor-payments/${r.id}/approve`);
      toast.success("Approved — payout initiated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Approve failed");
    }
  };

  const submitReject = async () => {
    if (!rejectDlg) return;
    if (!rejectReason.trim()) { toast.error("Reason is required"); return; }
    try {
      await api.post(`/vendor-payments/${rejectDlg.id}/reject`, { reason: rejectReason });
      toast.success("Rejected");
      setRejectDlg(null);
      setRejectReason("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reject failed");
    }
  };

  const cancelReq = async (r) => {
    if (!window.confirm("Cancel this pending payment request?")) return;
    try {
      await api.delete(`/vendor-payments/${r.id}`);
      toast.success("Cancelled");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Cancel failed");
    }
  };

  return (
    <div data-testid="vendor-payments-page" className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <QrCode className="h-7 w-7 text-red-600" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Senior Management</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Vendor Scan & Pay
          </h1>
        </div>
        <Button onClick={() => { resetForm(); setNewDlgOpen(true); }} className="brand-bg brand-bg-hover text-white" data-testid="new-payment-btn">
          <QrCode className="h-4 w-4 mr-1.5" /> New Payment
        </Button>
      </div>

      <div
        className="bg-amber-50 border border-amber-300 rounded-lg px-3 py-2 text-xs text-amber-800 flex items-start gap-2"
        data-testid="mocked-payout-banner"
      >
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <span className="font-semibold">Payout is currently MOCKED.</span> Approving a
          request marks it "Paid" with a fake reference — no real money moves yet. This will
          switch to AU Bank's live API Banking transfer the moment credentials are provided.
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="text-sm text-slate-600">{rows.length} requests</div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 w-[160px] text-xs" data-testid="vp-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All statuses</SelectItem>
            <SelectItem value="PENDING">Pending</SelectItem>
            <SelectItem value="PAID">Paid</SelectItem>
            <SelectItem value="REJECTED">Rejected</SelectItem>
            <SelectItem value="FAILED">Failed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Payee</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2 text-left">Purpose</th>
                <th className="px-3 py-2 text-left">Requested By</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">No payment requests yet</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`vp-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-900">{r.payee_name || "—"}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{r.payee_vpa}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">{fmtINR(r.amount)}</td>
                  <td className="px-3 py-2 text-slate-600">{r.purpose || "—"}</td>
                  <td className="px-3 py-2 text-slate-600">
                    <div>{r.requested_by_name}</div>
                    <div className="text-[10px] text-slate-400">{r.requested_by_role}</div>
                  </td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className={`${STATUS_STYLE[r.status] || ""} border`}>
                      {r.status}
                    </Badge>
                    {r.status === "PAID" && (
                      <div className="text-[10px] text-slate-400 font-mono mt-0.5">{r.payout_reference}</div>
                    )}
                    {r.status === "REJECTED" && (
                      <div className="text-[10px] text-red-500 mt-0.5">{r.rejection_reason}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-1.5">
                      {canApprove && r.status === "PENDING" && (
                        <>
                          <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => approve(r)} data-testid={`approve-vp-${r.id}`}>
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => setRejectDlg(r)} data-testid={`reject-vp-${r.id}`}>
                            <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                          </Button>
                        </>
                      )}
                      {r.status === "PENDING" && (r.requested_by === user?.id || user?.role === "super_admin") && (
                        <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500"
                          onClick={() => cancelReq(r)} data-testid={`cancel-vp-${r.id}`}>
                          <Ban className="h-3.5 w-3.5 mr-1" /> Cancel
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* New payment dialog */}
      <Dialog open={newDlgOpen} onOpenChange={(v) => { setNewDlgOpen(v); if (!v) resetForm(); }}>
        <DialogContent className="max-w-lg bg-white" data-testid="new-payment-dialog">
          <DialogHeader>
            <DialogTitle>New Vendor Payment Request</DialogTitle>
            <DialogDescription>
              Scan the vendor's UPI QR or enter details manually. This goes to a
              Super Admin for approval before payout.
            </DialogDescription>
          </DialogHeader>

          <div className="flex gap-2">
            <Button size="sm" variant={mode === "scan" ? "default" : "outline"}
              className={mode === "scan" ? "brand-bg brand-bg-hover text-white" : ""}
              onClick={() => setMode("scan")} data-testid="mode-scan-btn">
              <ScanLine className="h-3.5 w-3.5 mr-1.5" /> Scan QR
            </Button>
            <Button size="sm" variant={mode === "manual" ? "default" : "outline"}
              className={mode === "manual" ? "brand-bg brand-bg-hover text-white" : ""}
              onClick={() => setMode("manual")} data-testid="mode-manual-btn">
              <IndianRupee className="h-3.5 w-3.5 mr-1.5" /> Manual Entry
            </Button>
          </div>

          {mode === "scan" && <ScanPanel onDecoded={onDecoded} />}

          <div className="grid grid-cols-2 gap-3 text-sm pt-1">
            <div className="col-span-2">
              <Label className="text-[10px] uppercase text-slate-500">UPI ID (VPA) *</Label>
              <Input value={form.payee_vpa}
                onChange={(e) => setForm({ ...form, payee_vpa: e.target.value })}
                placeholder="vendor@upi" className="h-8 mt-1 text-xs" data-testid="vp-vpa-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Payee Name</Label>
              <Input value={form.payee_name}
                onChange={(e) => setForm({ ...form, payee_name: e.target.value })}
                className="h-8 mt-1 text-xs" data-testid="vp-name-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-slate-500">Amount (₹) *</Label>
              <Input type="number" min="0" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="h-8 mt-1 text-xs" data-testid="vp-amount-input" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase text-slate-500">Purpose</Label>
              <Input value={form.purpose}
                onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                placeholder="e.g. Packing material invoice #123"
                className="h-8 mt-1 text-xs" data-testid="vp-purpose-input" />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase text-slate-500">Notes</Label>
              <Textarea rows={2} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-1 text-xs" data-testid="vp-notes-input" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setNewDlgOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="brand-bg brand-bg-hover text-white" data-testid="submit-vp-btn">
              {saving ? "Submitting…" : "Submit for Approval"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectDlg} onOpenChange={(v) => !v && setRejectDlg(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="reject-vp-dialog">
          <DialogHeader>
            <DialogTitle>Reject Payment Request</DialogTitle>
            <DialogDescription className="sr-only">Provide a reason for rejection.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Reason *</Label>
            <Textarea rows={3} value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="mt-1 text-xs" data-testid="reject-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDlg(null)}>Cancel</Button>
            <Button onClick={submitReject} className="bg-red-600 hover:bg-red-700 text-white" data-testid="submit-reject-vp-btn">
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
